import { bumpAuthRevision } from '@heidi/auth/state';

/**
 * The auth slice fields the revision is defined over. Typed structurally
 * rather than off `RootState`: the register lives outside the store on
 * purpose, and importing `@/store` here — even as a type — would put this
 * file back inside the Rematch graph it exists to keep the counter out of.
 */
type AuthRevisionPair = {
  token?: string | undefined;
  authProvider?: string | undefined;
};

/**
 * `auth` is optional because this attaches at module scope, before anything
 * has had a chance to read the store — and a store assembled from a stubbed
 * model registry (several component suites replace `@/models` wholesale)
 * carries no auth slice at all. "No slice" is simply "no pair yet": nothing
 * to observe, nothing to advance.
 */
type AuthRevisionStore = {
  getState: () => { auth?: AuthRevisionPair | undefined };
  subscribe: (listener: () => void) => () => void;
};

/**
 * Wires the one writer of the auth revision register: every store
 * notification where `(auth.token, auth.authProvider)` differs from the
 * previously observed pair advances it once, and nothing else does.
 *
 * Defining the revision over the pair rather than over the reducers that
 * write it is what makes it whole-slice accurate. redux-persist's rehydrate
 * merge (`autoMergeLevel1`) can write the slice without any reducer of ours
 * running; a per-reducer counter has to answer for that case separately,
 * whereas an observer of the pair cannot miss it.
 *
 * Redux notifies subscribers synchronously at the end of `dispatch`, so the
 * register is already current by the time a dispatch returns — the same
 * moment a reducer-held counter was current. Do not batch, debounce or defer
 * the bump: the readers pair the revision with a token inside one
 * continuation and a deferred write would hand them a mixed-time tuple.
 *
 * Returns the store's unsubscribe so a caller that owns a throwaway store
 * (a test, a second store in a future shell) can detach it again.
 */
export function attachAuthRevisionTracking(store: AuthRevisionStore): () => void {
  let observedToken = store.getState().auth?.token;
  let observedProvider = store.getState().auth?.authProvider;

  return store.subscribe(() => {
    const token = store.getState().auth?.token;
    const authProvider = store.getState().auth?.authProvider;
    if (token === observedToken && authProvider === observedProvider) return;
    observedToken = token;
    observedProvider = authProvider;
    bumpAuthRevision();
  });
}
